// API Service for Email Tester
//
// Auth: the BFF mints a JWT on /auth/login and returns it in the response body
// AND sets an httponly cookie on .levantar.ai. Chrome's third-party cookie
// blocking (Privacy Sandbox) silently drops the cookie for some users because
// bff.emailtest.levantar.ai is a different origin from emailtest.levantar.ai,
// causing a login → instantly "session expired" loop.
//
// Fix: we stash the JWT in localStorage on login and send it as
// `Authorization: Bearer <token>` on every request. The api-authorizer lambda
// already accepts Authorization headers (checks header before cookie), so no
// backend change is needed. The cookie is still set as a fallback for
// browsers that accept it.

let onUnauthorized = null

const AUTH_TOKEN_KEY = 'emailtest_auth_token'

export const setOnUnauthorized = (callback) => {
  onUnauthorized = callback
}

function getStoredToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY)
  } catch {
    return null
  }
}

function setStoredToken(token) {
  try {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token)
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY)
    }
  } catch {
    /* storage unavailable — cookie is the only auth path */
  }
}

const getApiUrl = () => {
  const hostname = window.location.hostname

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3001' // Local API proxy if needed
  }

  // Production
  if (hostname === 'emailtest.levantar.ai') {
    return 'https://bff.emailtest.levantar.ai'
  }

  // Preview environments: preview.emailtest.levantar.ai/{branch}/
  // Map to {branch}.bff.emailtest.levantar.ai
  if (hostname === 'preview.emailtest.levantar.ai') {
    const pathMatch = window.location.pathname.match(/^\/([^/]+)/)
    if (pathMatch) {
      const branch = pathMatch[1]
      return `https://${branch}.bff.emailtest.levantar.ai`
    }
  }

  // Fallback for any other subdomain
  return 'https://bff.emailtest.levantar.ai'
}

const apiUrl = getApiUrl()

const request = async (endpoint, options = {}) => {
  const url = `${apiUrl}${endpoint}`

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }
  // Primary auth path: Authorization header driven by localStorage token.
  // Cookie is still sent via credentials:'include' as a fallback.
  const token = getStoredToken()
  if (token && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`
  }

  const config = {
    credentials: 'include',
    ...options,
    headers,
  }

  const response = await fetch(url, config)

  if (!response.ok) {
    if (response.status === 401 && onUnauthorized) {
      // Treat a 401 as a hard sign-out: clear the stored token so stale JWTs
      // don't keep hitting the server.
      setStoredToken(null)
      onUnauthorized()
      throw new Error('Session expired')
    }
    const error = await response.json().catch(() => ({ message: 'Request failed' }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  return response.json()
}

// Auth endpoints
export const login = async (username, password) => {
  const data = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })
  // Stash the JWT so subsequent requests can send Authorization headers even
  // when the httponly cookie got dropped by third-party cookie blocking.
  if (data?.token) {
    setStoredToken(data.token)
  }
  return data
}

export const logout = async () => {
  try {
    return await request('/auth/logout', { method: 'POST' })
  } finally {
    setStoredToken(null)
  }
}

// Address endpoints
export const listAddresses = async () => {
  return request('/addresses')
}

export const createAddress = async () => {
  return request('/addresses', {
    method: 'POST'
  })
}

export const deleteAddress = async (email) => {
  return request(`/addresses/${encodeURIComponent(email)}`, {
    method: 'DELETE'
  })
}

// Email endpoints (use JWT auth via BFF cookies)
export const listEmails = async (email) => {
  return request(`/emails?email=${encodeURIComponent(email)}`)
}

export const getEmail = async (messageId, email) => {
  return request(`/emails/${encodeURIComponent(messageId)}?email=${encodeURIComponent(email)}`)
}

// Send an outbound email FROM one of your own addresses.
// Body: { to, from_email, from_name?, subject, text_body, html_body?, in_reply_to?, references? }
export const sendEmail = async (body) => {
  return request('/emails/send', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// API Key endpoints
export const createApiKey = async (email, alias) => {
  return request(`/addresses/${encodeURIComponent(email)}/api-keys`, {
    method: 'POST',
    body: JSON.stringify({ alias })
  })
}

export const listApiKeys = async (email) => {
  return request(`/addresses/${encodeURIComponent(email)}/api-keys`)
}

export const deleteApiKey = async (email, keyId) => {
  return request(`/addresses/${encodeURIComponent(email)}/api-keys/${keyId}`, {
    method: 'DELETE'
  })
}

// MFA Device endpoints
export const listMfaDevices = async () => {
  return request('/mfa-devices')
}

export const createMfaDevice = async (otpauthUri, name) => {
  return request('/mfa-devices', {
    method: 'POST',
    body: JSON.stringify({ otpauth_uri: otpauthUri, name: name || undefined })
  })
}

export const deleteMfaDevice = async (deviceId) => {
  return request(`/mfa-devices/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE'
  })
}

export const generateMfaCode = async (deviceId) => {
  return request(`/mfa-devices/${encodeURIComponent(deviceId)}/code`, {
    method: 'POST'
  })
}
